import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import InfoIcon from "../../images/icons/InfoIcon.svg";
import CheckIcon from "@mui/icons-material/Check";
import { TextNTooltip, TextOnly } from "../Content";
import {
  PharmacyCard,
  blackColor,
  DataGridListStyle,
  HtmlTooltip,
} from "../../Theme";
import PharmacyBenefitsIcon from "../../images/icons/PharmacyBenefitsIcon.svg";
import { v4 as uuid } from "uuid";
import { YSButton } from "../../uikit/YSButton";
import { useEffect, useState } from "react";
import {
  RTBPCHECK,
  RTBPCHECKPharmacyPopulate,
} from "../../Apis/AuthApiServices";
import IconButton from "@mui/material/IconButton";
import NoRecords from "../NoRecords";
import ErrorOverlay from "../ErrorOverlay";
import YSSnackBar from "../../uikit/YSSnackBar";
import { loadingPharmacyDropDown } from "../../Redux/Slice/PharmacyDropDownSlice";
import { useDispatch } from "react-redux";
import { CollapsibleList } from "../../common/ExpandableText";

const pharmayAltCol: GridColDef[] = [
  {
    field: "drugName",
    headerName: "Drug Name",
    width: 200,
    cellClassName: "w20",
    headerClassName: "w20",
    renderCell: (params: any) => <TextNTooltip Text={params.value} />,
  },
  {
    field: "pharmacyName",
    headerName: "Pharmacy Name",
    width: 200,
    cellClassName: "w20",
    headerClassName: "w20",
    renderCell: ({ row }) => (
      <>
        <TextOnly Text={row?.pharmacyName ?? "NA"} />
        <HtmlTooltip
          placement="top"
          arrow
          title={
            <>
              <Typography>
                <strong>Formulary Status: </strong>
                {row?.formularyStatus},<br />
                <strong>Drug Status Code: </strong>
                {row?.drugStatusCode}
              </Typography>
            </>
          }
        >
          <IconButton className="tooltip">
            <img src={InfoIcon} alt="Tooltip" loading="lazy" />
          </IconButton>
        </HtmlTooltip>
      </>
    ),
  },
  {
    field: "price",
    headerName: "Estimated Patient Pay",
    width: 70,
    cellClassName: "w15",
    headerClassName: "w15",
    renderCell: ({ row }) => (
      <>
        <TextOnly Text={row?.price ?? "NA"} />
        {row?.price && (
          <HtmlTooltip
            placement="top"
            arrow
            title={
              <>
                <Typography>
                  {row?.deductibleRemainingAmount && (
                    <>
                      <strong>Deducted Remaining Amount: </strong>
                      {row?.deductibleRemainingAmount},<br />
                    </>
                  )}
                  {row?.deductibleAppliedAmount && (
                    <>
                      <strong>Deducted Applied Amount:</strong>
                      {row?.deductibleAppliedAmount}
                    </>
                  )}
                </Typography>
              </>
            }
          >
            <IconButton className="tooltip">
              <img src={InfoIcon} alt="Tooltip" loading="lazy" />
            </IconButton>
          </HtmlTooltip>
        )}
      </>
    ),
  },
  {
    field: "patientCostPerDay",
    headerName: "Estimated Price/Quantity",
    width: 160,
    cellClassName: "w15",
    headerClassName: "w15",
    renderCell: ({ row }) => (
      <TextOnly
        Text={`${row.patientCostPerDay ? `$${row.patientCostPerDay}` : "NA"}`}
      />
    ),
  },
  {
    field: "quantity",
    headerName: "Quantity / Day Supply",
    width: 150,
    cellClassName: "w15",
    headerClassName: "w15",
    renderCell: ({ row }) => (
      <TextOnly Text={`${row.quantity ?? "NA"}/${row.daysSupply ?? "NA"}`} />
    ),
  },
  {
    field: "cov",
    headerName: "Coverage",
    width: 100,
    cellClassName: "w30",
    headerClassName: "w30",
    renderCell: ({ row }) => (
      <>
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            paddingBottom: 10,
          }}
        >
          {row?.coverageAlertDetails?.length > 0 ? (
            <CollapsibleList data={row?.coverageAlertDetails} />
          ) : (
            "NA"
          )}
        </Box>
      </>
    ),
  },
];

export default function PatientBenefitsCheck(props: any) {
  const pharmayCol: GridColDef[] = [
    {
      field: "pharmacyName",
      headerName: "Pharmacy Name",
      width: 200,
      cellClassName: "w25",
      headerClassName: "w25",
      renderCell: ({ row }) => (
        <Box style={{ display: "flex", flexDirection: "column" }}>
          <Box display="flex">
            <TextOnly sx={{ fontSize: 14 }} Text={row?.pharmacyName ?? "NA"} />
            {(row?.pharmacyType === "M" || row?.pharmacyType === "MAIL") && "-"}
            <Typography
              fontSize={14}
              variant="body2"
              component="span"
              color="#44799C"
            >
              {(row?.pharmacyType === "M" || row?.pharmacyType === "MAIL") &&
                "(MO)"}
            </Typography>
          </Box>

          <Typography variant="body2" component="div" marginTop={1}>
            {row?.formularyStatus && (
              <>
                {/* <strong>Formulary Status: </strong> */}
                <Typography fontSize={14} variant="body2" component="div">
                  {row?.formularyStatus ?? "NA"}
                </Typography>
              </>
            )}
          </Typography>
          {/* <Typography variant="body2" component="div">
            {row?.drugStatusCode && (
              <>
                <strong>Drug Status Code: </strong>
                <Typography variant="body2" component="div">
                  {row?.drugStatusCode ?? "NA"}
                </Typography>
              </>
            )}
          </Typography> */}
        </Box>
      ),
    },
    {
      field: "price",
      headerName: "Estimated Patient Pay",
      width: 160,
      cellClassName: "w13",
      headerClassName: "w13",
      renderCell: ({ row }) => (
        <>
          <TextOnly Text={row?.price ? `$${row?.price}` : "NA"} />
          {row?.price && (
            <HtmlTooltip
              placement="top"
              arrow
              title={
                <>
                  <Typography>
                    {row?.deductibleRemainingAmount && (
                      <>
                        <strong>Deducted Remaining Amount: </strong>
                        {row?.deductibleRemainingAmount},<br />
                      </>
                    )}
                    {row?.deductibleAppliedAmount && (
                      <>
                        <strong>Deducted Applied Amount:</strong>
                        {row?.deductibleAppliedAmount}
                      </>
                    )}
                  </Typography>
                </>
              }
            >
              <IconButton className="tooltip">
                <img src={InfoIcon} alt="Tooltip" loading="lazy" />
              </IconButton>
            </HtmlTooltip>
          )}
        </>
      ),
    },
    {
      field: "quantity",
      headerName: "Quantity / Day Supply",
      width: 160,
      cellClassName: "w13",
      headerClassName: "w13",
      renderCell: ({ row }) => (
        <TextOnly Text={`${row.quantity ?? "NA"}/${row.daysSupply ?? "NA"}`} />
      ),
    },
    {
      field: "patientCostPerDay",
      headerName: "Estimated Price/Quantity",
      width: 160,
      cellClassName: "w15",
      headerClassName: "w15",
      renderCell: ({ row }) => (
        <TextOnly
          Text={`${row.patientCostPerDay ? `$${row.patientCostPerDay}` : "NA"}`}
        />
      ),
    },
    {
      field: "coverageAlerts",
      headerName: "Coverage",
      width: 100,
      cellClassName: "w40",
      headerClassName: "w40",
      renderCell: ({ row }) => (
        <>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              paddingBottom: 10,
            }}
          >
            {row?.coverageAlertDetails?.length > 0 ? (
              <CollapsibleList data={row?.coverageAlertDetails} />
            ) : (
              "NA"
            )}
          </Box>
        </>
      ),
    },
    {
      field: "",
      headerName: "Action",
      width: 80,
      cellClassName: "w10",
      headerClassName: "w10",
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <YSButton
            className={""}
            children={"Change"}
            onClick={() => {
              rtpbPopulate(row);
            }}
            variant={"contained"}
            size={"small"}
          />
        );
      },
    },
  ];
  const dispatch = useDispatch();
  const { formData, setRtpbPharmacyPopData, handleHidePopupClick }: any = props;
  const [data, setData] = useState<any>([]);
  const [Alternate, setAlternate] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [stateData, setStateData] = useState<any>({
    message: "",
    open: false,
    variant: "",
  });
  const patientId = sessionStorage.getItem("patientId");
  const doctorId = localStorage.getItem("doctorId");

  const rtpbPopulate = async (prop: any) => {
    try {
      const bodyData = {
        prescriptionForm: formData,
        patientId: patientId,
        userDetails: {
          doctorId: doctorId,
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
        newFormularyStatusForRTPB: prop?.formularyStatus,
        messageIdOfRtpbResponse: prop?.messageIdOfRtpbResponse,
        newCoverageForRTPB: prop?.epaRequired
          ? "Prior Authorization required"
          : "",
        newQuantityForRTPB: prop?.quantity,
        newDaySupplyForRTPB: prop?.daysSupply,
        pharmacyType: prop?.pharmacyType,
        ncpdpId: prop?.ncpdpId,
      };
      const result = await RTBPCHECKPharmacyPopulate(bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        setRtpbPharmacyPopData(result?.data?.response);
        handleHidePopupClick();
        dispatch(loadingPharmacyDropDown());
      } else {
        setRtpbPharmacyPopData([]);
        setStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
      }
    } catch (eror) {
      setRtpbPharmacyPopData([]);
      setStateData((prevState: any) => {
        return {
          ...prevState,
          message: "try again later",
          variant: "error",
          open: true,
        };
      });
    }
  };

  const RtbpCheck = async () => {
    try {
      setLoading(true);
      const bodyData = {
        prescriptionForm: formData,
        patientId: patientId,
        userDetails: {
          doctorId: doctorId,
          onBehalfDoctorId: localStorage.getItem("onBehalf") ?? "",
          selectedClinicId: sessionStorage.getItem("clinicId"),
        },
      };
      const result = await RTBPCHECK(bodyData);
      if (result?.data?.digitalRxStatusCode === 200) {
        if (result?.data?.response[0]?.errorMessage) {
          setStateData((prevState: any) => {
            return {
              ...prevState,
              message: result?.data?.response?.map(
                (each: any) => `Rx-${each?.rxNumber}. ${each?.errorMessage} `
              ),
              variant: "error",
              open: true,
            };
          });
          setTimeout(() => {
            handleHidePopupClick();
          }, 2000);
        } else {
          const alternate = result?.data?.response?.filter(
            (each: any) => each?.type === "alternate"
          );
          const regular = result?.data?.response?.filter(
            (each: any) => each?.type === "regular"
          );

          setData(regular);
          setAlternate(alternate);
          setLoading(false);
          setError(false);
        }
        setLoading(false);
        setError(false);
      } else {
        setData([]);
        setLoading(false);
        setError(false);
        setStateData((prevState: any) => {
          return {
            ...prevState,
            message: result?.data?.digitalRxStatusMessage,
            variant: "error",
            open: true,
          };
        });
      }
    } catch (err) {
      setData([]);
      setLoading(false);
      setError(true);
      setStateData((prevState: any) => {
        return {
          ...prevState,
          message: "Try Again After Some Time!",
          variant: "error",
          open: true,
        };
      });
    }
  };
  useEffect(() => {
    RtbpCheck();
  }, []);

  return (
    <>
      {loading ? (
        <Grid
          container
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          height={"50vh"}
        >
          <CircularProgress color="primary" />
        </Grid>
      ) : (
        <PharmacyCard>
          <Grid container columnSpacing={2} spacing={2}>
            <Grid item xs={12} marginRight={2}>
              <Typography
                component="div"
                color={"primary"}
                variant="subtitle2"
                fontWeight={600}
              >
                Based on your pharmacy, drug, quantity & days supply selections,
                these are the available patient benefits.
              </Typography>
              <Typography
                component="div"
                variant="body2"
                style={{
                  alignItems: "center",
                }}
              >
                <Typography
                  component="span"
                  color={"primary"}
                  variant="inherit"
                  style={{ marginRight: 15, float: "left" }}
                >
                  <img src={PharmacyBenefitsIcon} alt="PBC" loading="lazy" />
                </Typography>{" "}
                <Typography component="div" color={blackColor} variant="body2">
                  <Typography
                    component="span"
                    variant="inherit"
                    fontWeight={600}
                  >
                    Note:
                  </Typography>{" "}
                  Pricing/coverage data is a point in time calculation based
                  upon the quantity and days supply provided in the request and
                  may vary once the prescription is filled at the dispensing
                  pharmacy. Any returned alternative information is
                  informational only and not intended to replace clinical
                  decisions.
                </Typography>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <DataGridListStyle className="half">
                <DataGrid
                  rows={data ?? []}
                  sortingOrder={["desc", "asc"]}
                  columns={pharmayCol}
                  autoHeight
                  getRowHeight={() => "auto"}
                  getRowId={(row) => uuid()}
                  headerHeight={36}
                  pageSize={10}
                  disableSelectionOnClick
                  autoPageSize
                  rowsPerPageOptions={[10]}
                  loading={loading}
                  components={{
                    NoRowsOverlay: NoRecords,
                    NoResultsOverlay: NoRecords,
                    ErrorOverlay: ErrorOverlay,
                  }}
                  error={error || undefined}
                />
              </DataGridListStyle>
            </Grid>
            <Grid item xs={12}>
              <DataGridListStyle className="half">
                <DataGrid
                  rows={Alternate ?? []}
                  sortingOrder={["desc", "asc"]}
                  columns={pharmayAltCol}
                  autoHeight
                  getRowHeight={() => "auto"}
                  getRowId={(row) => uuid()}
                  headerHeight={36}
                  pageSize={10}
                  disableSelectionOnClick
                  autoPageSize
                  rowsPerPageOptions={[10]}
                  loading={loading}
                  components={{
                    NoRowsOverlay: NoRecords,
                    NoResultsOverlay: NoRecords,
                    ErrorOverlay: ErrorOverlay,
                  }}
                  error={error || undefined}
                />
              </DataGridListStyle>
            </Grid>
          </Grid>
        </PharmacyCard>
      )}
      <YSSnackBar
        message={stateData.message}
        variant={stateData.variant}
        open={stateData.open}
        handleClose={() => {
          setStateData((prevState: any) => {
            return { ...prevState, open: false };
          });
        }}
      />
    </>
  );
}
